import { useState } from "react";
import { useForm } from "react-hook-form";
import { FaEnvelope, FaLock, FaCheckCircle, FaArrowLeft, FaEyeSlash, FaEye } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { useDispatch } from "react-redux";
import { forgotUserPassword, resetUserPassword } from "../../redux/ReduxSlices/authSlice";
import { useNotification } from "../../contextApi/NotificationProvider";

const ForgotPasswordPopup = ({ onClose }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: "", type: "" });
    const { showNotification } = useNotification();
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [userEmail, setUserEmail] = useState("");


    const dispatch = useDispatch();

    const {
        register: registerEmail,
        handleSubmit: handleEmailSubmit,
        formState: { errors: emailErrors },
    } = useForm();

    const {
        register: registerReset,
        handleSubmit: handlePasswordReset,
        watch,
        formState: { errors: resetErrors },
    } = useForm();

    const onEmailSubmit = async (data) => {
        setLoading(true);
        try {
            const response = await dispatch(forgotUserPassword({ email: data.email })).unwrap();
            if (response.success) {
                setUserEmail(data.email)
                setMessage({ text: "OTP sent to your email", type: "success" });
                setStep(2);
            } else {
                setMessage({ text: response.message, type: "error" });
            }
        } catch (error) {
            setMessage({ text: error.message || "Failed to send OTP", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const onPasswordReset = async (data) => {
        if (data.newPassword !== data.confirmPassword) {
            setMessage({ text: "Passwords don't match", type: "error" });
            return;
        }
        try {
            const payload = {
                email: userEmail,
                otp: data.otp,
                new_password: data.newPassword,
            }
            const response = await dispatch(resetUserPassword(payload)).unwrap();
            if (response.success) {
                showNotification("Password reset successfully!")
                onClose()
            } else {
                setMessage({ text: response.message, type: "error" });
            }
        } catch (error) {
            setMessage({ text: error.message || "Failed to send OTP", type: "error" });
        } finally {
            setLoading(false);
        }
        setLoading(true);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="p-6">
                    {message.text && (
                        <div
                            className={`mb-4 p-3 rounded-lg ${message.type === "success"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                                }`}
                        >
                            {message.text}
                        </div>
                    )}

                    {step === 1 ? (
                        <form onSubmit={handleEmailSubmit(onEmailSubmit)} className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-xl font-bold text-gray-800">Reset Password</h3>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <MdOutlineCancel size={20} />
                                </button>
                            </div>
                            <p className="text-sm text-gray-600">
                                Enter your email address to receive a verification code
                            </p>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaEnvelope className="text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        {...registerEmail("email", {
                                            required: "Email is required",
                                            pattern: {
                                                value: /^\S+@\S+$/i,
                                                message: "Invalid email address",
                                            },
                                        })}
                                        className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="you@example.com"
                                    />
                                </div>
                                {emailErrors.email && (
                                    <p className="text-sm text-red-600 mt-1">{emailErrors.email.message}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg shadow-md transition duration-300"
                            >
                                {loading ? "Sending OTP..." : "Send Verification Code"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handlePasswordReset(onPasswordReset)} className="space-y-4">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center space-x-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setStep(1);
                                            setMessage({ text: "", type: "" });
                                        }}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        <FaArrowLeft />
                                    </button>
                                    <h3 className="text-xl font-bold text-gray-800">Set New Password</h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    &times;
                                </button>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Verification Code <span className="text-sm text-gray-500" >(OTP set over email)</span>
                                </label>
                                <input
                                    type="text"
                                    {...registerReset("otp", { required: "Verification code is required" })}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    placeholder="Enter 6-digit code"
                                />
                                {resetErrors.otp && (
                                    <p className="text-sm text-red-600 mt-1">{resetErrors.otp.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    New Password
                                </label>
                                <div className="relative">
                                    {/* Lock icon on the left */}
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaLock className="text-gray-400" />
                                    </div>

                                    {/* Toggle Eye Icon */}
                                    <div
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                                        onClick={() => setShowNewPassword((prev) => !prev)}
                                    >
                                        {showNewPassword ? (
                                            <FaEyeSlash className="text-gray-400" />
                                        ) : (
                                            <FaEye className="text-gray-400" />
                                        )}
                                    </div>

                                    {/* Input Field */}
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        {...registerReset("newPassword", {
                                            required: "New password is required",
                                            validate: (value) => {
                                                const rules = [
                                                    { regex: /[A-Z]/, message: "Must include an uppercase letter" },
                                                    { regex: /[a-z]/, message: "Must include a lowercase letter" },
                                                    { regex: /[0-9]/, message: "Must include a number" },
                                                    {
                                                        regex: /[^A-Za-z0-9]/,
                                                        message: "Must include a special character",
                                                    },
                                                ];

                                                for (let rule of rules) {
                                                    if (!rule.regex.test(value)) return rule.message;
                                                }
                                                return true;
                                            },
                                        })}
                                        className="pl-10 pr-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="••••••••"
                                    />
                                </div>

                                {resetErrors.newPassword && (
                                    <p className="text-sm text-red-600 mt-1">
                                        {resetErrors.newPassword.message}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirm New Password
                                </label>
                                <div className="relative">
                                    {/* Left icon */}
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaCheckCircle className="text-gray-400" />
                                    </div>

                                    {/* Toggle Eye Icon */}
                                    <div
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    >
                                        {showConfirmPassword ? (
                                            <FaEyeSlash className="text-gray-400" />
                                        ) : (
                                            <FaEye className="text-gray-400" />
                                        )}
                                    </div>

                                    {/* Input Field */}
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        {...registerReset("confirmPassword", {
                                            required: "Confirm your password",
                                            validate: (value) =>
                                                value === watch("newPassword") || "Passwords do not match",
                                        })}
                                        className="pl-10 pr-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                        placeholder="••••••••"
                                    />
                                </div>

                                {resetErrors.confirmPassword && (
                                    <p className="text-sm text-red-600 mt-1">
                                        {resetErrors.confirmPassword.message}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg shadow-md transition duration-300"
                            >
                                {loading ? "Resetting Password..." : "Reset Password"}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPopup;
